import { useSearchParams } from "react-router-dom";
import { useOtpLogs } from "../hooks/use-otp-logs";
import { OtpLogTable } from "./otp-log-table";
import { OtpSummaryTags } from "./otp-summary-tags";
import OtpFilters from "./otp-filters";
import { SearchBar } from "@/components/common/search-bar";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { XIcon } from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import { OtpLogFilters } from "@/pages/otp-log/types/otp-log";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import OtpAnalyticsList from "./otp-analytics-list";

export default function OtpLogList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const activeTab = searchParams.get("tab") || "logs";
  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";
  const event_type = searchParams.get("event_type") || "ALL";
  const status = searchParams.get("status") || "ALL";
  const channel = searchParams.get("channel") || "ALL";
  const startDate = searchParams.get("startDate") || undefined;
  const endDate = searchParams.get("endDate") || undefined;
  const userId = searchParams.get("userId") || undefined;

  const handleTabChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", value);
    setSearchParams(newSearchParams);
  };

  const filters: OtpLogFilters = {
    page,
    search: search || undefined,
    event_type: event_type as OtpLogFilters["event_type"],
    status: status as OtpLogFilters["status"],
    channel: channel as OtpLogFilters["channel"],
    startDate,
    endDate,
    userId,
  };

  const { data, isLoading, isFetching, error, refetch } = useOtpLogs(filters);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["otp-logs"] });
    queryClient.invalidateQueries({ queryKey: ["otp-analytics"] });
    queryClient.invalidateQueries({ queryKey: ["otp-stats"] });
    refetch();
  };

  // Filter out SMS channels - only show EMAIL and WHATSAPP
  const logs = (data?.logs || []).filter(
    (log) => log.channel !== "SMS"
  );
  const currentPage = data?.pagination?.currentPage || 0;
  const lastPage = data?.pagination?.lastPage || 0;

  return (
    <ContentLayout
      header={
        <div className="flex items-center gap-2 flex-wrap">
          <OtpFilters />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search by user, identifier, or session token..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />,
              }}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      }
      data={logs}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load OTP logs"
      emptyState={{
        message:
          "No OTP logs found. Try adjusting your search criteria or filters.",
      }}
      containerClassName="h-full p-6 w-full"
      headerClassName="space-y-4"
      contentClassName="space-y-6 mt-6"
    >
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="logs">Logs</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>
          <OtpSummaryTags startDate={startDate} endDate={endDate} />
        </div>

        <TabsContent value="logs" className="mt-6">
          <div className="space-y-6">
            <div>
              <OtpLogTable logs={logs} isLoading={isFetching} />
            </div>

            {/* Pagination */}
            {logs.length > 0 && (
              <CustomPaginationComponent
                currentPage={currentPage}
                lastPage={lastPage + 1}
                onPageChange={(newPage) => {
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.set("page", newPage.toString());
                  setSearchParams(newSearchParams);
                }}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <OtpAnalyticsList />
        </TabsContent>
      </Tabs>
    </ContentLayout>
  );
}

